<script setup>
import Icon from './ui/Icon.vue'
import Ring from './ui/Ring.vue'
import { fmtGlobal as fmt, goalStates, hideGlobal } from '../stores'
import { openSheet, state } from '../stores/state'
import { shortDay } from '../lib/dates'
import { cadenceKey } from '../lib/savings'
import { t } from '../stores/i18n'

/** What the goal sets aside, and when it next does it. */
const pace = (g) => {
  if (g.complete) return t('goals.reached')
  if (!g.next) return t('goals.toGo', { amount: fmt(g.remaining) })
  return t('goals.pace', {
    amount: fmt(g.perInstalment),
    cadence: t(cadenceKey(g.cadence)).toLowerCase(),
    day: shortDay(g.next),
  })
}

const focusGoal = (g) => openSheet('goal', { id: g.goal.id })
</script>

<template>
  <section class="card overflow-hidden">
    <header class="flex items-center gap-3 border-b border-line-soft px-4 py-3.5">
      <div class="min-w-0 flex-1">
        <h2 class="text-[15px] font-semibold">{{ t('goals.title') }}</h2>
        <p class="mt-0.5 text-[12px] text-faint">
          {{
            goalStates.length
              ? t('goals.reachedOf', {
                  done: goalStates.filter((g) => g.complete).length,
                  total: goalStates.length,
                })
              : t('goals.empty')
          }}
        </p>
      </div>
      <button class="btn-soft !py-2" @click="openSheet('goal')">
        <Icon name="plus" :size="16" /> {{ t('common.add') }}
      </button>
    </header>

    <ul v-if="goalStates.length" class="divide-y divide-line-soft">
      <li v-for="g in goalStates" :key="g.goal.id" class="px-4 py-3.5">
        <div class="flex items-center gap-3">
          <div class="relative grid shrink-0 place-items-center">
            <Ring :value="g.progress" :size="50" :color="g.complete ? 'var(--c-accent)' : 'var(--c-goal)'" />
            <span class="tnum absolute text-[11px] font-bold">
              {{ hideGlobal ? '••' : `${Math.round(g.progress * 100)}%` }}
            </span>
          </div>

          <button class="min-w-0 flex-1 text-left" @click="focusGoal(g)">
            <span class="flex min-w-0 items-center gap-1.5">
              <span class="truncate text-[14.5px] font-semibold">{{ g.goal.name }}</span>
              <span v-if="g.complete" class="tag bg-accent-soft text-accent">{{ t('goals.done') }}</span>
            </span>
            <span class="tnum mt-0.5 block text-[12.5px] text-muted">
              {{ t('common.xOfY', { x: fmt(g.saved, { round: true }), y: fmt(g.target, { round: true }) }) }}
            </span>
            <span class="mt-0.5 block truncate text-[12px] text-faint">{{ pace(g) }}</span>
          </button>

          <div class="flex shrink-0 items-center">
            <button
              class="btn-ghost !px-1.5 !py-1"
              :aria-label="t('common.editThing', { name: g.goal.name })"
              @click="openSheet('goal', { id: g.goal.id })"
            >
              <Icon name="pencil" :size="15" />
            </button>
          </div>
        </div>

        <div class="mt-3 h-1.5 overflow-hidden rounded-full bg-raised">
          <div
            class="h-full rounded-full transition-all duration-500"
            :class="g.complete ? 'bg-accent' : 'bg-goal'"
            :style="{ width: `${Math.max(2, g.progress * 100)}%` }"
          />
        </div>
      </li>
    </ul>

    <div v-else class="px-6 py-10 text-center">
      <span class="mx-auto grid size-12 place-items-center rounded-2xl bg-goal-soft text-goal">
        <Icon name="target" :size="22" />
      </span>
      <p class="mt-3 text-[14px] font-medium">{{ t('stats.noGoals') }}</p>
      <p class="mt-1 text-[13px] text-muted">{{ t('goals.emptyHint') }}</p>
      <button class="btn-primary mx-auto mt-4" @click="openSheet('goal')">
        <Icon name="plus" :size="16" /> {{ t('goals.newGoal') }}
      </button>
    </div>
  </section>
</template>
